import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Progress } from '@/components/ui/progress';
import { BookOpen, ChevronDown, ChevronRight, LogOut, LayoutDashboard } from 'lucide-react';

export default function ReaderLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [chapters, setChapters] = useState([]);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentPath = location.pathname.replace('/resource/', '');
  const isAdmin = user?.role === 'admin';

  const fetchChapters = useCallback(async () => {
    try {
      const res = await api.get('/chapters');
      setChapters(res.data);
      // Auto-expand the chapter containing the current resource
      res.data.forEach(ch => {
        if (ch.resources?.some(r => `/resource/${r.url_path}` === location.pathname)) {
          setExpandedChapters(prev => ({ ...prev, [ch.id]: true }));
        }
      });
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [location.pathname]);

  useEffect(() => { fetchChapters(); }, [fetchChapters]);

  const toggleChapter = (chId) => {
    setExpandedChapters(prev => ({ ...prev, [chId]: !prev[chId] }));
  };

  const totalResources = chapters.reduce((sum, ch) => sum + ch.resource_count, 0);
  const completedResources = chapters.reduce((sum, ch) => sum + ch.completed_count, 0);

  return (
    <div className="flex h-screen overflow-hidden" data-testid="reader-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[280px] bg-[#111] flex flex-col transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        {/* Sidebar header */}
        <div className="px-5 py-5 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-2.5">
            <BookOpen className="w-5 h-5 text-white" />
            <div>
              <h1 className="text-white font-bold text-[15px] leading-tight">Untangling AI</h1>
              <p className="text-[#888] text-xs">Course Content</p>
            </div>
          </div>
        </div>

        {/* Chapter navigation */}
        <nav className="flex-1 overflow-y-auto sidebar-scroll py-2" data-testid="sidebar-nav">
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin" /></div>
          ) : chapters.map(ch => {
            const isExpanded = expandedChapters[ch.id];
            return (
              <div key={ch.id} data-testid={`sidebar-chapter-${ch.number}`}>
                <button onClick={() => toggleChapter(ch.id)}
                  className="w-full flex items-start gap-2 px-5 py-2.5 hover:bg-[#1a1a1a] transition-colors text-left"
                  data-testid={`sidebar-chapter-toggle-${ch.number}`}
                >
                  {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-[#F5C518] mt-0.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-[#F5C518] mt-0.5 shrink-0" />}
                  <div>
                    <span className="text-[#F5C518] text-xs font-semibold">Chapter {ch.number}</span>
                    <p className="text-white text-[13px] leading-snug mt-0.5">{ch.name.split(' — ')[1] || ch.name}</p>
                  </div>
                </button>
                {isExpanded && (
                  <div className="pb-1">
                    {ch.resources?.map(res => {
                      const isActive = location.pathname === `/resource/${res.url_path}`;
                      return (
                        <button key={res.id}
                          onClick={() => { navigate(`/resource/${res.url_path}`); setSidebarOpen(false); }}
                          className={`w-full text-left pl-10 pr-4 py-2.5 transition-colors ${isActive ? 'bg-[#F5C518] text-black' : 'hover:bg-[#1a1a1a] text-[#ccc]'}`}
                          data-testid={`sidebar-resource-${res.url_path}`}
                        >
                          <span className={`text-xs font-semibold block ${isActive ? 'text-black/70' : 'text-[#888]'}`}>{res.url_path}</span>
                          <span className={`text-[13px] leading-snug block ${isActive ? 'text-black font-medium' : ''}`}>{res.title}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="border-t border-[#2a2a2a] px-5 py-3 space-y-2">
          {isAdmin && (
            <button onClick={() => navigate('/admin')} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#F5C518] bg-[#F5C518]/10 hover:bg-[#F5C518]/20 rounded transition-colors" data-testid="admin-panel-button">
              <LayoutDashboard className="w-3.5 h-3.5" /> Admin Panel
            </button>
          )}
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#888] hover:text-white hover:bg-[#1a1a1a] rounded transition-colors" data-testid="logout-button">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top progress bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0" data-testid="progress-header">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1 -ml-1" data-testid="mobile-menu-button">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            <span className="text-sm text-gray-500 font-medium">Course Progress</span>
          </div>
          <span className="text-sm font-semibold text-[#D4A910]" data-testid="progress-count">
            {completedResources} of {totalResources} lessons completed
          </span>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-white">
          {children}
        </main>
      </div>
    </div>
  );
}
